import { MACKINAC_XRPC_URL } from '@/constants/api'
import { getMastodonAccountId, type MastodonSession } from '@/lib/mastodon'

const STORAGE_KEY = 'mackinac_mastodon_server_sessions'
const EXPIRY_BUFFER_MS = 60_000

export interface MastodonExtendedBio {
    headline: string | null
    description: string | null
    website: string | null
    location: string | null
    pronouns: string | null
    updatedAt: string
}

export interface MastodonServerSession {
    token: string
    expiresAt: string
    remoteAccountId: string
}

export type MastodonBioErrorKind = 'reconnect' | 'rateLimited' | 'unavailable' | 'failed'

export class MastodonBioError extends Error {
    readonly kind: MastodonBioErrorKind

    constructor(kind: MastodonBioErrorKind) {
        super(`Mastodon extended bio request failed: ${kind}`)
        this.name = 'MastodonBioError'
        this.kind = kind
    }
}

const sessionCache = new Map<string, MastodonServerSession>()

function readStoredSessions(): Record<string, MastodonServerSession> {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}') as Record<
            string,
            MastodonServerSession
        >
    } catch {
        return {}
    }
}

function persistSessions(sessions: Record<string, MastodonServerSession>): void {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(sessions))
    } catch {
        // Persistence is an optimization; the in-memory session remains usable.
    }
}

function cacheSession(accountId: string, session: MastodonServerSession): void {
    sessionCache.set(accountId, session)
    persistSessions({ ...readStoredSessions(), [accountId]: session })
}

export function getCachedMastodonServerSession(
    accountId: string
): MastodonServerSession | undefined {
    const cached = sessionCache.get(accountId) ?? readStoredSessions()[accountId]
    if (cached) sessionCache.set(accountId, cached)
    return cached
}

export function evictMastodonServerSession(accountId: string): void {
    sessionCache.delete(accountId)
    const stored = readStoredSessions()
    delete stored[accountId]
    persistSessions(stored)
}

export function splitApHandle(handle: string): { instance: string; acct: string } {
    const normalized = handle.trim().replace(/^@/, '')
    const separator = normalized.lastIndexOf('@')
    if (separator <= 0 || separator === normalized.length - 1) {
        throw new MastodonBioError('failed')
    }
    const acct = normalized.slice(0, separator)
    const instance = normalized.slice(separator + 1).toLowerCase()
    if (acct.includes('@') || instance.includes('/') || instance.includes(':')) {
        throw new MastodonBioError('failed')
    }
    return { instance, acct }
}

export function ownBioKey(session: MastodonSession): { instance: string; acct: string } {
    return {
        instance: new URL(session.instance_url).hostname.toLowerCase(),
        acct: session.account.username,
    }
}

async function responseError(response: Response): Promise<MastodonBioError> {
    let code: unknown
    try {
        code = ((await response.clone().json()) as { error?: unknown }).error
    } catch {
        // The status code still gives us a useful fallback classification.
    }
    if (response.status === 401 && code === 'InvalidMastodonToken') {
        return new MastodonBioError('reconnect')
    }
    if (response.status === 429) return new MastodonBioError('rateLimited')
    if (response.status === 503) return new MastodonBioError('unavailable')
    return new MastodonBioError('failed')
}

export async function fetchMastodonExtendedBio(
    instance: string,
    acct: string
): Promise<MastodonExtendedBio | null> {
    const response = await fetch(
        `${MACKINAC_XRPC_URL}/mastodon/bio/${encodeURIComponent(instance)}/${encodeURIComponent(acct)}`
    )
    if (response.status === 404) return null
    if (!response.ok) throw await responseError(response)
    return ((await response.json()) as { bio: MastodonExtendedBio }).bio
}

export async function bootstrapMastodonServerSession(
    session: MastodonSession
): Promise<MastodonServerSession> {
    const accountId = getMastodonAccountId(session)
    const response = await fetch(`${MACKINAC_XRPC_URL}/mastodon/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instance: session.instance_url, accessToken: session.access_token }),
    })
    if (!response.ok) {
        const error = await responseError(response)
        if (error.kind === 'reconnect') evictMastodonServerSession(accountId)
        throw error
    }
    const data = (await response.json()) as {
        token: string
        expiresAt: string
        account: { remoteAccountId: string }
    }
    if (data.account.remoteAccountId !== String(session.account.id)) {
        evictMastodonServerSession(accountId)
        throw new MastodonBioError('reconnect')
    }
    const cached = {
        token: data.token,
        expiresAt: data.expiresAt,
        remoteAccountId: data.account.remoteAccountId,
    }
    cacheSession(accountId, cached)
    return cached
}

function isUsable(session: MastodonServerSession | undefined): session is MastodonServerSession {
    return Boolean(session && Date.parse(session.expiresAt) - EXPIRY_BUFFER_MS > Date.now())
}

export async function withMastodonServerSession(
    session: MastodonSession,
    fn: (token: string) => Promise<Response>
): Promise<Response> {
    const accountId = getMastodonAccountId(session)
    let serverSession = getCachedMastodonServerSession(accountId)
    if (!isUsable(serverSession)) {
        evictMastodonServerSession(accountId)
        serverSession = await bootstrapMastodonServerSession(session)
    }
    let response = await fn(serverSession.token)
    if (response.status !== 401) return response

    evictMastodonServerSession(accountId)
    serverSession = await bootstrapMastodonServerSession(session)
    response = await fn(serverSession.token)
    if (response.status === 401) {
        evictMastodonServerSession(accountId)
        throw new MastodonBioError('failed')
    }
    return response
}

export async function saveMastodonExtendedBio(
    session: MastodonSession,
    // Each field is independently optional: PUT replaces the whole bio, so any
    // field the caller omits falls back to its current server value rather
    // than being cleared. This lets the extended-bio editor (heading/content)
    // and the general edit-profile form (location, pronouns) each write only
    // the fields they own without stomping the other's.
    input: { heading?: string; content?: string; location?: string; pronouns?: string }
): Promise<MastodonExtendedBio> {
    const key = ownBioKey(session)
    const current = await fetchMastodonExtendedBio(key.instance, key.acct)
    const response = await withMastodonServerSession(session, token =>
        fetch(`${MACKINAC_XRPC_URL}/mastodon/bio`, {
            method: 'PUT',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({
                headline: (input.heading ?? current?.headline ?? '').trim() || undefined,
                description: (input.content ?? current?.description ?? '').trim() || undefined,
                website: current?.website ?? undefined,
                location: (input.location ?? current?.location ?? '').trim() || undefined,
                pronouns: (input.pronouns ?? current?.pronouns ?? '').trim() || undefined,
            }),
        })
    )
    if (!response.ok) throw await responseError(response)
    return ((await response.json()) as { bio: MastodonExtendedBio }).bio
}
